import { useAuth } from '@choco-admin/auth';
import { Spinner } from '@heroui/react';
import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import {
  clearPostLoginRedirect,
  readPostLoginRedirect,
} from '../utils/postLoginRedirect';

/**
 * Clears the recorded destination in an effect rather than during render:
 * StrictMode renders twice, and a render that cleared it would leave the second
 * render with nothing to redirect to.
 */
const PostLoginRedirect: React.FC<{ to: string }> = ({ to }) => {
  useEffect(() => {
    clearPostLoginRedirect();
  }, []);
  return <Navigate to={to} replace />;
};

interface AuthGuardProps {
  children: React.ReactNode;
  redirectTo?: string;
}

/**
 * AuthGuard Component
 *
 * Protects routes by checking authentication status.
 * Redirects unauthenticated users to login page.
 * Shows loading state while authentication is being checked.
 * Sends a freshly logged-in user on to the page they were headed for (see
 * `postLoginRedirect`), since the OAuth flow returns to the site root.
 */
const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  redirectTo = '/login',
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-default-50">
        <div className="text-center">
          <Spinner size="lg" color="primary" />
          <p className="mt-4 text-default-600 text-base">
            Checking authentication...
          </p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  const postLoginRedirect = readPostLoginRedirect();
  if (postLoginRedirect) {
    return <PostLoginRedirect to={postLoginRedirect} />;
  }

  // Render children if authenticated
  return <>{children}</>;
};

export default AuthGuard;
